if (!customElements.get('mlv-back-in-stock')) {
  customElements.define('mlv-back-in-stock', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('[data-bis-form]');
      this.email = this.form.elements.email;
      this.status = this.querySelector('[data-bis-status]');
      this.submit = this.querySelector('[data-bis-submit]');
      this.entry = this.querySelector('[data-bis-entry]');
      this.variant = {
        id: this.dataset.variantId,
        title: this.dataset.variantTitle,
        available: this.dataset.available === 'true'
      };
      this.update(this.variant);
      this.querySelector('[data-bis-open]').addEventListener('click', () => this.open(), { signal });
      this.querySelector('[data-bis-close]').addEventListener('click', () => this.dialog.close(), { signal });
      this.dialog.addEventListener('click', (event) => {
        const rect = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < rect.left || event.clientX > rect.right ||
            event.clientY < rect.top || event.clientY > rect.bottom)) this.dialog.close();
      }, { signal });
      this.dialog.addEventListener('close', () => {
        this.sequence = (this.sequence || 0) + 1;
        this.form.reset();
        this.clearStatus();
        this.opener?.focus();
      }, { signal });
      this.email.addEventListener('input', () => this.clearStatus(), { signal });
      this.form.addEventListener('submit', (event) => this.onSubmit(event), { signal });
      document.addEventListener('variant:change', (event) => {
        if (event.target?.getAttribute('id') !== this.dataset.formId) return;
        if (this.dialog.open) this.dialog.close();
        this.update(event.detail.variant);
      }, { signal });
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.controller?.abort();
      this.sequence = (this.sequence || 0) + 1;
    }

    update(variant) {
      this.variant = variant;
      const valid = Boolean(variant?.id);
      this.entry.hidden = !valid || variant.available;
      this.hidden = !valid || variant.available;
      this.querySelector('[data-bis-variant]').textContent = variant?.title === 'Default Title' ? '' : variant?.title || '';
      this.dataset.selectedVariant = valid ? String(variant.id) : '';
    }

    open() {
      if (!this.variant?.id || this.variant.available) return;
      this.opener = document.activeElement;
      this.clearStatus();
      this.submit.disabled = false;
      this.dialog.showModal();
      this.email.focus({ preventScroll: true });
    }

    clearStatus() {
      this.status.hidden = true;
      this.status.textContent = '';
      this.email.removeAttribute('aria-invalid');
      this.submit.disabled = Boolean(this.pending);
    }

    buildRequest(email) {
      return {
        url: `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(this.dataset.companyId)}`,
        options: {
          method: 'POST',
          headers: { 'Content-Type': 'application/vnd.api+json', revision: '2026-10-15' },
          body: JSON.stringify({ data: {
            type: 'back-in-stock-subscription',
            attributes: { profile: { data: { type: 'profile', attributes: { email } } }, channels: ['EMAIL'] },
            relationships: { variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${this.variant.id}` } } }
          } })
        }
      };
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.pending) return;
      this.email.value = this.email.value.trim();
      if (!this.email.validity.valid) {
        this.status.textContent = this.dataset.invalidMessage;
        this.status.hidden = false;
        this.email.setAttribute('aria-invalid', 'true');
        this.email.focus();
        return;
      }
      if (!this.variant?.id || this.variant.available) {
        this.status.textContent = this.dataset.unavailableMessage;
        this.status.hidden = false;
        return;
      }
      const request = this.buildRequest(this.email.value);
      const variantId = String(this.variant.id);
      const sequence = this.sequence = (this.sequence || 0) + 1;
      this.pending = true;
      this.submit.disabled = true;
      this.email.readOnly = true;
      this.status.textContent = this.dataset.loadingMessage;
      this.status.hidden = false;
      this.dataset.requestStatus = 'pending';
      try {
        const stockResponse = await fetch(`${this.dataset.productUrl}.js`, {
          cache: 'no-store', signal: AbortSignal.timeout(12000)
        });
        if (!stockResponse.ok) throw new Error('stock');
        const product = await stockResponse.json();
        const current = product.variants.find(variant => String(variant.id) === variantId);
        if (!current || current.available) {
          if (sequence === this.sequence) {
            this.status.textContent = this.dataset.unavailableMessage;
            this.dataset.requestStatus = 'available';
          }
          return;
        }
        if (sequence !== this.sequence || String(this.variant?.id) !== variantId) return;
        const response = await fetch(request.url, {
          ...request.options, signal: AbortSignal.timeout(15000)
        });
        if (sequence !== this.sequence) return;
        if (response.status !== 202) {
          this.status.textContent = response.status === 429 ? this.dataset.rateMessage : this.dataset.errorMessage;
          this.dataset.requestStatus = 'error';
          return;
        }
        this.status.textContent = this.dataset.successMessage;
        this.dataset.requestStatus = 'accepted';
        this.dataset.subscribedVariant = variantId;
        this.email.value = '';
      } catch {
        if (sequence === this.sequence) {
          this.status.textContent = this.dataset.errorMessage;
          this.dataset.requestStatus = 'error';
        }
      } finally {
        this.pending = false;
        this.email.readOnly = false;
        this.submit.disabled = this.dataset.requestStatus === 'accepted';
      }
    }
  });
}
